import { applyCleValidationErrorsToFormik } from "@/api/cle-api/errors";
import Button from "@/components/bricks/button/Button";
import { CleApiErrorAlert } from "@/components/cle-api/CleApiErrorAlert";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import IsRequired from "@/components/formcontrols/common/IsRequired";
import Modal, { ModalContext } from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import type {
  DispensationEvidenceModel,
  UpdateDispensationRequest,
} from "@/api/cle-api";
import { useUpdateDispensation } from "@/hooks/cle-hooks";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo } from "react";
import * as Yup from "yup";
import { YupNumberOfLessonsRequired } from "../../../../../util/yup";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { formatEfteruddannelsePeriodLabel } from "./dispensationUtils";

interface EditDispensationFormValues {
  externalEfteruddanelseperiodeId: string;
  numberOfLessons: string;
}

const validationSchema = Yup.object({
  externalEfteruddanelseperiodeId: Yup.string().required(
    "Efteruddannelsesperiode skal vælges",
  ),
  numberOfLessons: YupNumberOfLessonsRequired(),
});

const dispensationToFormValues = (
  dispensation: DispensationEvidenceModel,
): EditDispensationFormValues => ({
  externalEfteruddanelseperiodeId:
    dispensation.externalEfteruddanelseperiodeId ?? "",
  numberOfLessons:
    dispensation.numberOfLessons != null
      ? String(dispensation.numberOfLessons)
      : "",
});

const formValuesToUpdateRequest = (
  values: EditDispensationFormValues,
  initialValues: EditDispensationFormValues,
): UpdateDispensationRequest => {
  const body: UpdateDispensationRequest = {};

  if (
    values.externalEfteruddanelseperiodeId !==
    initialValues.externalEfteruddanelseperiodeId
  ) {
    body.external_efteruddanelseperiode_id =
      values.externalEfteruddanelseperiodeId;
  }

  const numberOfLessons = Number(values.numberOfLessons);
  const initialNumberOfLessons = Number(initialValues.numberOfLessons);
  if (numberOfLessons !== initialNumberOfLessons) {
    body.number_of_lessons = numberOfLessons;
  }

  return body;
};

interface EditDispensationFormProps {
  dispensation: DispensationEvidenceModel;
  periods: DecoratedPersonEfteruddannelse[];
  userId: string;
  onSave: (updated: DispensationEvidenceModel) => void;
}

const EditDispensationForm = ({
  dispensation,
  periods,
  userId,
  onSave,
}: EditDispensationFormProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const {
    mutate: updateDispensation,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateDispensation();

  const periodOptions = useMemo(
    () =>
      periods.map((period) => ({
        value: period.id,
        label: formatEfteruddannelsePeriodLabel(period),
      })),
    [periods],
  );

  const initialFormValues = useMemo(
    () => dispensationToFormValues(dispensation),
    [dispensation],
  );

  const formik = useFormik<EditDispensationFormValues>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const body = formValuesToUpdateRequest(values, initialFormValues);
      if (Object.keys(body).length === 0) return;

      updateDispensation(
        {
          userId,
          dispensationId: dispensation.id,
          body,
        },
        {
          onSuccess: (updated) => {
            onSave(updated);
            resetMutation();
            setModalOpen(false);
          },
          onError: (err) => {
            applyCleValidationErrorsToFormik(err, formik.setFieldError);
          },
        },
      );
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    resetMutation();
    setModalOpen(false);
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            <div className="flex-100">
              <label
                className="form-label"
                htmlFor="externalEfteruddanelseperiodeId"
              >
                Efteruddannelsesperiode
                <IsRequired />
              </label>
              <select
                className={`form-select${
                  formik.touched.externalEfteruddanelseperiodeId &&
                  formik.errors.externalEfteruddanelseperiodeId
                    ? " is-invalid"
                    : ""
                }`}
                id="externalEfteruddanelseperiodeId"
                name="externalEfteruddanelseperiodeId"
                value={formik.values.externalEfteruddanelseperiodeId}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              >
                <option value="">Vælg periode</option>
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ValidationErrorMessage
                touched={formik.touched.externalEfteruddanelseperiodeId}
                errorMessage={formik.errors.externalEfteruddanelseperiodeId}
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="numberOfLessons">
                Antal lektioner
                <IsRequired />
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.numberOfLessons &&
                  formik.errors.numberOfLessons
                    ? " is-invalid"
                    : ""
                }`}
                id="numberOfLessons"
                min="0"
                name="numberOfLessons"
                step="any"
                type="number"
                value={formik.values.numberOfLessons}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.numberOfLessons}
                errorMessage={formik.errors.numberOfLessons}
              />
            </div>
          </div>
          {isError && (
            <div className="c-modal-container-inner">
              <CleApiErrorAlert
                error={error}
                fallback="Kunne ikke gemme opdatering."
              />
            </div>
          )}
          <StickyFooter className="border-top">
            <Button color="secondary" type="button" onClick={handleCancel}>
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button
                color="primary"
                disabled={isPending || !formik.dirty}
                type="submit"
              >
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

interface EditDispensationModalProps {
  dispensation: DispensationEvidenceModel;
  periods: DecoratedPersonEfteruddannelse[];
  userId: string;
  onSave: (updated: DispensationEvidenceModel) => void;
}

export const EditDispensationModal = ({
  dispensation,
  periods,
  userId,
  onSave,
}: EditDispensationModalProps) => (
  <Modal title="Rediger dispensation">
    <EditDispensationForm
      dispensation={dispensation}
      periods={periods}
      userId={userId}
      onSave={onSave}
    />
  </Modal>
);
